import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styles from './AddStakeholders.module.css';
import { AuthBrandedLoader } from '../../Login/AuthBrandedLoader';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import clsx from 'clsx';
import * as Yup from 'yup';
import {
  addStakeholder,
  deleteStakeholder,
  getStakeholders,
  getTenantDetails,
  updateTenantDetails,
  updateStakeholder,
} from '../../../auth/_requests'; // ✅ include getStakeholders
import { useAuth } from '../../../auth/Auth';
import { postLoginTask } from '../../../helpers/utils/login';
import { Icon } from '../../../helpers/Icons/Icon';
import { trackOnboardingStepCompleted } from '../../../helpers/utils/analytics';

interface Stakeholder {
  tenantStakeholderId: string;
  firstName: string;
  lastName: string;
  email: string;
}

// Validation schema
const getStakeholderSchema = (existingStakeholders: Stakeholder[], editingId?: string | null) => {
  return Yup.object().shape({
    firstName: Yup.string().trim().required('First name is required'),
    lastName: Yup.string().trim().required('Last name is required'),
    email: Yup.string()
      .trim()
      .email('Invalid email format')
      .matches(/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/, 'Invalid email format')
      .min(3, 'Minimum 3 characters')
      .max(50, 'Maximum 50 characters')
      .required('Email is required')
      .test('unique-email', 'This email is already added', (value) => {
        if (!value) return true;
        // Exclude the current stakeholder being edited from the uniqueness check
        return !existingStakeholders.some(
          (s) =>
            s.email.toLowerCase() === value.toLowerCase().trim() &&
            s.tenantStakeholderId !== editingId
        );
      }),
  });
};

const AddStakeholders = () => {
  const navigate = useNavigate();
  const { auth, saveAuth } = useAuth();
  const [loading, setLoading] = useState(false);
  const [hasStakeholders, setHasStakeholders] = useState<boolean | null>(null);
  const [stakeholders, setStakeholders] = useState<Stakeholder[]>([]);
  const [editingStakeholderId, setEditingStakeholderId] = useState<string | null>(null);
  const token = auth?.data?.token;

  // Fetch stakeholders on mount
  useEffect(() => {
    const fetchStakeholders = async () => {
      try {
        setLoading(true);
        const response = await getStakeholders(token);
        if (response?.data?.length) {
          setStakeholders(response.data);
          setHasStakeholders(true);
        } else {
          setHasStakeholders(false);
        }
      } catch (error) {
        console.error('Error fetching stakeholders:', error);
      } finally {
        setLoading(false);
      }
    };
    if (token) fetchStakeholders();
  }, [token]);

  //  Formik setup
  const formik = useFormik({
    initialValues: { firstName: '', lastName: '', email: '' },
    enableReinitialize: true,
    validationSchema: getStakeholderSchema(stakeholders, editingStakeholderId),
    onSubmit: async (values, { resetForm, setSubmitting, setStatus }) => {
      try {
        setLoading(true);
        if (editingStakeholderId) {
          await updateStakeholder(token, editingStakeholderId, values);
          setEditingStakeholderId(null);
        } else {
          await addStakeholder(token, values);
        }
        // Re-fetch stakeholders after adding/updating
        const response = await getStakeholders(token);
        const updatedStakeholders = response.data || [];
        setStakeholders(updatedStakeholders);
        resetForm();
      } catch (error) {
        console.error(`Error ${editingStakeholderId ? 'updating' : 'adding'} stakeholder:`, error);
        setStatus(
          `Failed to ${editingStakeholderId ? 'update' : 'add'} stakeholder. Please try again.`
        );
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  const handleEditStakeholder = (stakeholder: Stakeholder) => {
    setEditingStakeholderId(stakeholder.tenantStakeholderId);
    formik.setValues({
      firstName: stakeholder.firstName,
      lastName: stakeholder.lastName,
      email: stakeholder.email,
    });
  };

  const handleCancelEdit = () => {
    setEditingStakeholderId(null);
    formik.resetForm();
  };

  const handleRemoveStakeholder = async (id: string) => {
    try {
      setLoading(true);
      await deleteStakeholder(token, id);
      setStakeholders((prev) => prev.filter((s) => s.tenantStakeholderId !== id));
    } catch (error) {
      console.error('Error deleting stakeholder:', error);
    } finally {
      setLoading(false);
    }
  };
  const handleComplete = async () => {
    const tenantDetails = await getTenantDetails(auth?.data?.token);

    if (tenantDetails?.data?.tenant?.accountType === 'personal') {
      await updateTenantDetails(auth?.data?.token, {
        tenantDetails: {
          accountType: 'both',
        },
      });
    }

    if (auth) {
      trackOnboardingStepCompleted('add_stakeholders');
      await postLoginTask(auth, navigate, saveAuth);
    }
  };

  if (loading) return <AuthBrandedLoader message='Loading…' />;

  return (
    <section>
      <motion.main className={`${styles.main} auth-onboarding-card`} initial={false}>
        <div className={styles.formOuter}>
          <div className='space-y-6 ph-no-capture' data-clarity-mask='true'>
            <p className='text-white mb-6'>
              Add individuals with 25% or more ownership or directorship
            </p>

            {/* Yes/No selection */}
            <div className='mb-6'>
              <label className='block text-sm font-medium text-white mb-3'>
                Do you have stakeholders to add?
              </label>
              <div className='flex gap-4'>
                <button
                  type='button'
                  onClick={() => setHasStakeholders(true)}
                  className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-all ${
                    hasStakeholders === true
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  Yes
                </button>
                <button
                  type='button'
                  onClick={() => setHasStakeholders(false)}
                  className={`flex-1 py-3 px-4 border-2 rounded-lg font-medium transition-all ${
                    hasStakeholders === false
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-slate-200 text-slate-700 hover:border-slate-300'
                  }`}
                >
                  No
                </button>
              </div>
            </div>

            {/* Stakeholder Form */}
            {hasStakeholders === true && !editingStakeholderId && (
              <form className='space-y-4' noValidate onSubmit={formik.handleSubmit}>
                <div className='p-6 bg-black/50 rounded-xl space-y-4'>
                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                    {/* First Name */}
                    <div className='inputWrapper'>
                      <label className='label'>
                        First name<span className='text-red-500'>*</span>
                      </label>
                      <div
                        className={clsx(
                          formik.touched.firstName && formik.errors.firstName
                            ? styles.isInvalid
                            : '',
                          formik.touched.firstName && !formik.errors.firstName ? styles.valid : '',
                          'inputField'
                        )}
                      >
                        <input
                          type='text'
                          {...formik.getFieldProps('firstName')}
                          placeholder='John'
                        />
                      </div>
                      {formik.touched.firstName && formik.errors.firstName && (
                        <div className='text-red-500 text-sm mt-1'>{formik.errors.firstName}</div>
                      )}
                    </div>

                    {/* Last Name */}
                    <div className='inputWrapper'>
                      <label className='label'>
                        Last name<span className='text-red-500'>*</span>
                      </label>
                      <div
                        className={clsx(
                          formik.touched.lastName && formik.errors.lastName ? styles.isInvalid : '',
                          formik.touched.lastName && !formik.errors.lastName ? styles.valid : '',
                          'inputField'
                        )}
                      >
                        <input
                          type='text'
                          {...formik.getFieldProps('lastName')}
                          placeholder='Doe'
                        />
                      </div>
                      {formik.touched.lastName && formik.errors.lastName && (
                        <div className='text-red-500 text-sm mt-1'>{formik.errors.lastName}</div>
                      )}
                    </div>
                  </div>

                  {/* Email */}
                  <div className='inputWrapper'>
                    <label className='label'>
                      Email address<span className='text-red-500'>*</span>
                    </label>
                    <div
                      className={clsx(
                        formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                        formik.touched.email && !formik.errors.email ? styles.valid : '',
                        'inputField'
                      )}
                    >
                      <input
                        type='email'
                        {...formik.getFieldProps('email')}
                        placeholder='john.doe@example.com'
                      />
                    </div>
                    {formik.touched.email && formik.errors.email && (
                      <div className='text-red-500 text-sm mt-1'>{formik.errors.email}</div>
                    )}
                  </div>

                  <button
                    type='submit'
                    disabled={!formik.isValid || formik.isSubmitting}
                    className='w-full py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-slate-300 disabled:cursor-not-allowed'
                  >
                    Add stakeholder
                  </button>
                </div>
              </form>
            )}

            {/* Stakeholders List */}
            {stakeholders.length > 0 && (
              <div className='space-y-2 '>
                <h3 className='text-sm font-medium text-white'>
                  Added stakeholders ({stakeholders.length})
                </h3>
                {stakeholders.map((s) => (
                  <div key={s.tenantStakeholderId}>
                    {editingStakeholderId === s.tenantStakeholderId ? (
                      <div className='p-4 border-2 border-blue-500 rounded-lg space-y-4'>
                        <h4 className='text-sm font-medium text-white'>Edit stakeholder</h4>
                        <form className='space-y-4' noValidate onSubmit={formik.handleSubmit}>
                          <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                            {/* First Name */}
                            <div className='inputWrapper'>
                              <label className='label'>
                                First name<span className='text-red-500'>*</span>
                              </label>
                              <div
                                className={clsx(
                                  formik.touched.firstName && formik.errors.firstName
                                    ? styles.isInvalid
                                    : '',
                                  formik.touched.firstName && !formik.errors.firstName
                                    ? styles.valid
                                    : '',
                                  'inputField'
                                )}
                              >
                                <input
                                  type='text'
                                  {...formik.getFieldProps('firstName')}
                                  placeholder='John'
                                />
                              </div>
                              {formik.touched.firstName && formik.errors.firstName && (
                                <div className='text-red-500 text-sm mt-1'>
                                  {formik.errors.firstName}
                                </div>
                              )}
                            </div>

                            {/* Last Name */}
                            <div className='inputWrapper'>
                              <label className='label'>
                                Last name<span className='text-red-500'>*</span>
                              </label>
                              <div
                                className={clsx(
                                  formik.touched.lastName && formik.errors.lastName
                                    ? styles.isInvalid
                                    : '',
                                  formik.touched.lastName && !formik.errors.lastName
                                    ? styles.valid
                                    : '',
                                  'inputField'
                                )}
                              >
                                <input
                                  type='text'
                                  {...formik.getFieldProps('lastName')}
                                  placeholder='Doe'
                                />
                              </div>
                              {formik.touched.lastName && formik.errors.lastName && (
                                <div className='text-red-500 text-sm mt-1'>
                                  {formik.errors.lastName}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Email */}
                          <div className='inputWrapper'>
                            <label className='label'>
                              Email address<span className='text-red-500'>*</span>
                            </label>
                            <div
                              className={clsx(
                                formik.touched.email && formik.errors.email ? styles.isInvalid : '',
                                formik.touched.email && !formik.errors.email ? styles.valid : '',
                                'inputField'
                              )}
                            >
                              <input
                                type='email'
                                {...formik.getFieldProps('email')}
                                placeholder='john.doe@example.com'
                              />
                            </div>
                            {formik.touched.email && formik.errors.email && (
                              <div className='text-red-500 text-sm mt-1'>{formik.errors.email}</div>
                            )}
                          </div>

                          <div className='flex gap-2'>
                            <button
                              type='button'
                              onClick={handleCancelEdit}
                              className='flex-1 py-2 px-4 border border-slate-300 rounded-lg bg-slate-50 font-medium transition-colors'
                            >
                              Cancel
                            </button>
                            <button
                              type='submit'
                              disabled={!formik.isValid || formik.isSubmitting}
                              className='flex-1 py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:bg-slate-300 disabled:text-black disabled:cursor-not-allowed'
                            >
                              {formik.isSubmitting ? 'Saving...' : 'Save'}
                            </button>
                          </div>
                        </form>
                      </div>
                    ) : (
                      <div className='flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg'>
                        <div>
                          <p className='font-medium text-slate-900'>
                            {s.firstName} {s.lastName}
                          </p>
                          <p className='text-sm text-slate-600'>{s.email}</p>
                        </div>
                        <div className='flex gap-2'>
                          <button
                            type='button'
                            onClick={() => handleEditStakeholder(s)}
                            className='px-3 py-1 text-xs text-blue-600 hover:text-blue-700 border border-blue-300 rounded hover:bg-blue-50 font-medium'
                          >
                            Edit
                          </button>
                          <button
                            type='button'
                            onClick={() => handleRemoveStakeholder(s.tenantStakeholderId)}
                            className='text-red-600 hover:text-red-700 text-sm font-medium'
                          >
                            <Icon name='bin' className='text-[#B70D0D]' strokeWidth={0.2} />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Navigation */}
            <div className='flex gap-4 pt-4'>
              <button onClick={() => navigate('/kyb/verify')} type='button' className='cancelBtn'>
                Back
              </button>
              <button
                onClick={handleComplete}
                type='button'
                disabled={hasStakeholders === null}
                className='submitBtn'
              >
                Complete setup
              </button>
            </div>
          </div>
        </div>
      </motion.main>
    </section>
  );
};

export default AddStakeholders;
